import type { EsquemaFiltros } from './url-filtros';

/**
 * A1 · Los filtros que acepta cada pantalla, declarados en un solo lugar.
 *
 * Están acá y no dentro de cada pantalla por tres razones:
 *
 *  1. El tablero (A5) y el copiloto (B4) necesitan armar enlaces filtrados
 *     **sin montar la pantalla**. Si el esquema viviera dentro del componente,
 *     habría que importar la pantalla entera para construir un enlace.
 *  2. Son la base del MAPA DEL SISTEMA de la Fase A8: "qué filtros acepta cada
 *     pantalla" sale de acá, no de una lista escrita a mano que se desactualiza.
 *  3. Obliga a que el mismo concepto se llame igual en todas partes. La
 *     auditoría A0 encontró el filtro de persona como `employeeId` en Trabajos y
 *     `tecnicoId` en Despachos, y el rango de fechas con tres formas distintas.
 *
 * El orden de las claves define el orden de los parámetros en la URL.
 */

/** El rango de fechas, idéntico en toda la app. Ver `lib/rango-fechas`. */
const RANGO: EsquemaFiltros = {
  rango: { tipo: 'texto' },
  desde: { tipo: 'texto' },
  hasta: { tipo: 'texto' },
};

/** Conversaciones. Reemplaza al store de zustand que había. */
export const ESQUEMA_CONVERSACIONES: EsquemaFiltros = {
  // A5: reproduce el numero de "calientes sin atender" del tablero con la
  // MISMA condicion del backend, para que la tarjeta y la lista cuadren.
  sinAtender: { tipo: 'booleano' },
  status: { tipo: 'texto' },
  leadStatus: { tipo: 'texto' },
  channelId: { tipo: 'texto' },
  type: { tipo: 'texto' },
  search: { tipo: 'texto' },
};

/**
 * Trabajos. Antes: `fecha` + `desde` + `hasta` sueltas y `employeeId`.
 * Ahora: el rango unificado y `tecnico`, el mismo nombre que en Despachos.
 */
export const ESQUEMA_TRABAJOS: EsquemaFiltros = {
  estado: { tipo: 'texto' },
  tecnico: { tipo: 'texto' },
  // Búsqueda y orden viven en la URL como cualquier otro filtro: así un enlace
  // a "Trabajos de Ana ordenados por cita" se puede mandar por WhatsApp y el
  // que lo abre ve exactamente lo mismo.
  buscar: { tipo: 'texto' },
  orden: { tipo: 'texto' },
  ...RANGO,
  vencidos: { tipo: 'booleano' },
  fueraDeRadio: { tipo: 'booleano' },
  // Solo garantías: la lista del encargado de garantías. Va en la URL para que
  // el atajo del menú sea un enlace que se pueda compartir.
  garantia: { tipo: 'booleano' },
  pruebas: { tipo: 'booleano' },
  // A5: "Trabajos de hoy" del tablero cuenta solo la operacion de CAMPO -- un
  // caso remoto no ocupa tecnico ni ruta (S-04). Sin este filtro el numero
  // nunca podria cuadrar con el listado.
  campo: { tipo: 'booleano' },
};

/**
 * Despachos. Tiene UN solo filtro de pantalla: el rango, con default
 * "hoy y manana".
 *
 * La primera version de este esquema listaba tambien vendedor, formaPago,
 * comprobante, compania y tecnico. Estaba mal: esos son campos del modal de
 * edicion de un despacho, no filtros del listado. Se corrigio al inventariar la
 * pantalla antes de migrarla.
 */
export const ESQUEMA_DESPACHOS: EsquemaFiltros = {
  ...RANGO,
};

/** Pendientes del Vendedor. */
export const ESQUEMA_PENDIENTES: EsquemaFiltros = {
  estado: { tipo: 'texto' },
  vendedor: { tipo: 'texto' },
  ventas: { tipo: 'booleano' },
};

/**
 * Clientes. El booleano `esteMes` se reemplaza por el rango unificado
 * (`?rango=mes`), que dice lo mismo y se lee igual que en las demas pantallas.
 * `compro` es tri-estado: ausente = todos.
 */
export const ESQUEMA_CLIENTES: EsquemaFiltros = {
  search: { tipo: 'texto' },
  zona: { tipo: 'texto' },
  compro: { tipo: 'texto' },
  ...RANGO,
};

/**
 * CRM · Pipeline.
 *
 * `overStatus` NO entra: es estado de hover, no un filtro.
 *
 * `vista` (kanban/tabla) si entra, aunque sea una preferencia y no un filtro.
 * Vivia en localStorage y sigue siendo el default; la URL solo la PISA cuando
 * viene explicita. Asi un enlace puede decir "mirá esto en tabla" sin que la
 * preferencia de cada uno se pierda al navegar normalmente.
 */
export const ESQUEMA_CRM: EsquemaFiltros = {
  search: { tipo: 'texto' },
  tagId: { tipo: 'texto' },
  modelo: { tipo: 'texto' },
  vista: { tipo: 'texto' },
  sort: { tipo: 'texto' },
};

/** Contactos. */
export const ESQUEMA_CONTACTOS: EsquemaFiltros = {
  search: { tipo: 'texto' },
};

/** Tracking. */
export const ESQUEMA_TRACKING: EsquemaFiltros = {
  zona: { tipo: 'texto' },
};

/**
 * Usuarios. La pantalla no tenía NINGÚN filtro: pedía 100 cuentas y las pintaba
 * todas. Con veinte cuentas, encontrar a alguien ya es leer la lista entera.
 *
 * `estado` es texto y no un booleano porque tiene tres valores de verdad
 * («activos e inactivos», «solo activos», «solo inactivos»), y el ausente
 * —los dos— es justo el que no se escribe en la URL.
 */
export const ESQUEMA_USUARIOS: EsquemaFiltros = {
  buscar: { tipo: 'texto' },
  rol: { tipo: 'texto' },
  estado: { tipo: 'texto' },
};

/**
 * Desempeño de técnicos. Solo el rango unificado y la columna por la que se
 * ordena: la pantalla es una tabla de lectura, no un listado que se recorta.
 *
 * `orden` viaja en la URL igual que `sort` en el CRM: así «mirá esta tabla
 * ordenada por fichas incompletas» se manda por WhatsApp y el otro ve lo mismo.
 */
export const ESQUEMA_DESEMPENO: EsquemaFiltros = {
  ...RANGO,
  orden: { tipo: 'texto' },
};

/**
 * Todas las pantallas con filtros, por ruta.
 *
 * La Fase A8 lo consume para construir el mapa del sistema, y la A5 para saber
 * a qué pantalla puede llevar cada número del tablero.
 */
export const ESQUEMAS_POR_RUTA: Record<string, EsquemaFiltros> = {
  '/conversations': ESQUEMA_CONVERSACIONES,
  '/jobs': ESQUEMA_TRABAJOS,
  '/despachos': ESQUEMA_DESPACHOS,
  '/pendientes': ESQUEMA_PENDIENTES,
  '/clientes': ESQUEMA_CLIENTES,
  '/crm': ESQUEMA_CRM,
  '/contacts': ESQUEMA_CONTACTOS,
  '/tracking': ESQUEMA_TRACKING,
  '/users': ESQUEMA_USUARIOS,
  '/desempeno': ESQUEMA_DESEMPENO,
};
